import React from 'react';
import { Box, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { formatDateForDisplay } from '../utils/dateUtils';
import { esOperaciones, useWaGroups } from '../hooks/useWaGroups';

// Estado de la consulta automática al Precalificador Interno de Cofidis
// (Cloud Function onCofidisCaseCreated). Solo lectura: el envío lo hace el
// servidor al crearse el formulario completo.
const describe = (rec) => {
  const attempts = rec.attempts ? ` (intento ${rec.attempts})` : '';
  switch (rec.status) {
    case 'enviado':
      return {
        Icon: CheckCircleIcon,
        color: 'success.main',
        text: `Enviado a Cofidis · ${formatDateForDisplay(rec.sentAt)}`,
        detail: rec.message,
      };
    case 'enviando':
      return { Icon: HourglassEmptyIcon, color: 'info.main', text: 'Enviando a Cofidis…', detail: '' };
    case 'pendiente':
      return {
        Icon: HourglassEmptyIcon,
        color: 'warning.main',
        text: `Pendiente de reintento${attempts}`,
        detail: rec.lastError,
      };
    case 'error':
      return { Icon: ErrorOutlineIcon, color: 'error.main', text: `Error tras ${rec.attempts || 0} intentos`, detail: rec.lastError };
    case 'rechazado':
      return { Icon: ErrorOutlineIcon, color: 'error.main', text: 'Rechazado por el formulario de Cofidis', detail: rec.message };
    case 'duplicado':
      return {
        Icon: InfoOutlinedIcon,
        color: 'text.secondary',
        text: 'No enviado: este DNI ya se consultó en otro caso reciente',
        detail: rec.duplicateOf ? `Caso ${rec.duplicateOf}` : '',
      };
    case 'omitido':
      return { Icon: InfoOutlinedIcon, color: 'text.secondary', text: 'No enviado', detail: rec.reason };
    case 'revisar':
      return { Icon: ErrorOutlineIcon, color: 'warning.main', text: 'Revisar a mano en el portal de Cofidis', detail: rec.message || rec.reason };
    default:
      return { Icon: InfoOutlinedIcon, color: 'text.secondary', text: rec.status || 'Sin estado', detail: '' };
  }
};

const CofidisPrecalStatus = ({ record }) => {
  // Diego (07/09): esto lo ven solo Operaciones y dirección, no los comerciales.
  // Mientras no se sabe el grupo (null) tampoco se enseña, para que no asome un
  // instante al cargar.
  const groups = useWaGroups();
  if (!record || !esOperaciones(groups)) return null;
  const rec = record.cofidisPrecal;
  let view;
  if (rec) {
    view = describe(rec);
  } else if (record.source === 'client-portal') {
    view = { Icon: InfoOutlinedIcon, color: 'text.secondary', text: 'Sin consulta automática (formulario anterior al automatismo)', detail: '' };
  } else {
    view = { Icon: InfoOutlinedIcon, color: 'text.secondary', text: 'No aplica (caso creado desde el panel)', detail: '' };
  }
  const { Icon, color, text, detail } = view;
  return (
    <Box sx={{ mt: 2, pt: 1.5, borderTop: '1px dashed var(--ed-line, #e9ecef)' }}>
      <Typography variant="caption" sx={{ fontWeight: 700, color: 'var(--ed-text-2, #495057)', letterSpacing: '0.5px', display: 'block', mb: 0.5 }}>
        PRECALIFICADOR COFIDIS
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <Icon fontSize="small" sx={{ color }} />
        <Typography variant="body2" sx={{ color, fontWeight: 600 }}>{text}</Typography>
      </Box>
      {detail && (
        <Typography variant="caption" sx={{ display: 'block', mt: 0.25, color: 'text.secondary', wordBreak: 'break-word' }}>
          {detail}
        </Typography>
      )}
    </Box>
  );
};

export default CofidisPrecalStatus;
